import React from 'react';
import Joi from 'joi';
import PropTypes from 'prop-types';

import Form from '../../common/form';
import ListGroup from '../../blocks/list-group';
import VIObservationItemForm from './viObservationItemForm';
import VIRecommendationItemForm from './viRecommendationItemForm';

import { IMAGE_LIMITS } from '../../../utils/constants';
import { getRiskLevelsAsOption } from '../../../utils/data';
import { getFileUploadTypes } from '../../../utils/utilities';
import { SelectOption, VIReportItem } from '../../../utils/models';

type VIReportData = Omit<VIReportItem, 'id'>;

class VIReportDataItemForm extends Form {
	state = {
		data: {
			tagNumber: '',
			observations: [],
			recommendations: [],
			level: 'minor',
			images: [],
		},
		errors: {},
		loading: false,
		progress: 0,
	};

	schema = {
		tagNumber: Joi.string()
			.min(3)
			.required()
			.label('Identification / Tag Number'),
		observations: Joi.array()
			.min(1)
			.items(
				Joi.object({
					id: Joi.number().required().label('S/N'),
					name: Joi.string().min(3).required().label('Observation / Result'),
				})
			)
			.required()
			.label('Observation / Result'),
		recommendations: Joi.array()
			.min(1)
			.items(
				Joi.object({
					id: Joi.number().required().label('S/N'),
					name: Joi.string().min(3).required().label('Observation / Result'),
				})
			)
			.required()
			.label('Recommendation / Action'),
		level: Joi.string().required().label('Criticality'),
		images: Joi.array().min(1).max(IMAGE_LIMITS.GENERAL_PHOTOS).required().label('Anomaly Image'),
	};

	static propTypes = {
		onAdd: PropTypes.func,
	};

	handleAddObservation = (name: string) => {
		const data = { ...this.state.data } as VIReportData;
		const newObservation = { id: data.observations.length + 1, name };

		data.observations = [...data.observations, newObservation];
		this.setState({ data });
	};

	handleAddRecommendation = (name: string) => {
		const data = { ...this.state.data } as VIReportData;
		const newRecommendation = { id: data.recommendations.length + 1, name };

		data.recommendations = [...data.recommendations, newRecommendation];
		this.setState({ data });
	};

	handleDeleteObservation = ({ id }: SelectOption) => {
		const data = { ...this.state.data } as VIReportData;
		data.observations = data.observations.filter((o) => o.id !== id);

		this.setState({ data });
	};

	handleDeleteRecommendation = ({ id }: SelectOption) => {
		const data = { ...this.state.data } as VIReportData;
		data.recommendations = data.recommendations.filter((o) => o.id !== id);

		this.setState({ data });
	};

	doSubmit = (): void => {
		this.props.onAdd(this.state.data);

		this.setState({
			data: {
				tagNumber: '',
				observations: [],
				recommendations: [],
				level: 'minor',
				images: [],
			},
		});
	};

	render() {
		return (
			<React.Fragment>
				<form autoComplete="off" onSubmit={this.handleSubmit}>
					{this.renderInput({
						type: 'text',
						name: 'tagNumber',
						placeholder: 'Enter Identification / Tag Number',
						label: 'Identification / Tag Number',
					})}

					<div className="divider my-4"></div>

					<div className="row">
						<div className="col">
							<VIObservationItemForm
								onAddObservation={this.handleAddObservation}
							/>

							<ListGroup
								data={this.state.data.observations}
								onRemove={this.handleDeleteObservation}
							/>
						</div>

						<div className="col">
							<VIRecommendationItemForm
								onAddRecommendation={this.handleAddRecommendation}
							/>

							<ListGroup
								data={this.state.data.recommendations}
								onRemove={this.handleDeleteRecommendation}
							/>
						</div>
					</div>

					<div className="divider my-4"></div>

					<div className="row">
						<div className="col">
							{this.renderSelect({
								name: 'level',
								placeholder: 'Select Criticality',
								label: 'Criticality',
								options: getRiskLevelsAsOption(),
							})}
						</div>

						<div className="col">
							{this.renderFileInput({
								max: IMAGE_LIMITS.GENERAL_PHOTOS,
								multiple: IMAGE_LIMITS.GENERAL_PHOTOS > 1,
								name: 'images',
								accept: 'image/*,application/pdf',
								label: 'Anomaly Image',
								sublabel: 'Upload photos showing anomaly',
								types: getFileUploadTypes(),
							})}
						</div>
					</div>

					<div className="divider my-4"></div>

					<div className="d-flex justify-content-end">
						{this.renderButton('Add Item')}
					</div>
				</form>
			</React.Fragment>
		);
	}
}

export default VIReportDataItemForm;
